'use client';

import { useState, useEffect } from 'react';

import type { ContactFormFieldConfig } from '../interfaces';

export interface UseHubSpotFormFieldsResult {
  /** The resolved field config — either fetched from HubSpot or the static default. */
  fields: ReadonlyArray<ContactFormFieldConfig>;
  /** True while the fields are being fetched from the API. */
  loading: boolean;
  /** Non-null when the fetch failed. The static default fields are used as a fallback. */
  error: string | null;
}

/**
 * Fetches form field definitions from the HubSpot Forms V2 API via
 * the server-side route `/api/hubspot/form-fields`.
 *
 * - When `formId` is provided: fetches dynamically and renders whatever
 *   fields are configured in that HubSpot form.
 * - When `formId` is undefined/empty: sets an error state.
 *
 * Emits an error if fetching fails or no formId is passed.
 */
export function useHubSpotFormFields(formId?: string): UseHubSpotFormFieldsResult {
  const [fields, setFields] = useState<ReadonlyArray<ContactFormFieldConfig>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // No formId — immediately throw an error
    if (!formId) {
      setFields([]);
      setLoading(false);
      setError('Contact form is not configured correctly. Please provide a valid HubSpot Form ID.');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`/api/hubspot/form-fields?formId=${encodeURIComponent(formId)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: { fields?: ReadonlyArray<ContactFormFieldConfig> }) => {
        if (cancelled) return;
        if (data.fields && data.fields.length > 0) {
          setFields(data.fields);
        } else {
          // HubSpot returned an empty field list. Handle error.
          setError('Failed to load contact form fields. No fields returned.');
          setFields([]);
        }
      })
      .catch((err: Error) => {
        if (cancelled) return;
        console.error('[useHubSpotFormFields] fetch failed:', err.message);
        setError('Failed to load contact form fields. Wait a moment and refresh.');
        setFields([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Re-fetch if the formId changes (e.g. Sanity live preview updates).
  }, [formId]);
  return { fields, loading, error };
}
